// src/components/HistoricoMovimentacoesModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { useEmpresaId } from "../context/EmpresaContext";

const db = getFirestore();

function formatDate(date) {
  if (!date) return "";
  // Firestore Timestamp
  if (date?.toDate) return date.toDate().toLocaleString("pt-BR");
  // {seconds:nanoseconds} ou Date/string
  if (date?.seconds) return new Date(date.seconds * 1000).toLocaleString("pt-BR");
  return new Date(date).toLocaleString("pt-BR");
}

const HistoricoMovimentacoesModal = ({ open, produto, onClose }) => {
  const empresaId = useEmpresaId();
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Carrega em tempo real as movimentações do produto dentro da EMPRESA
  useEffect(() => {
    if (!open || !produto?.id || !empresaId) return;

    setLoading(true);
    const q = query(
      collection(db, "empresas", empresaId, "produtos", produto.id, "movimentacoes"),
      orderBy("data", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setMovimentacoes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false)
    );

    return () => unsub();
  }, [open, produto?.id, empresaId]);

  if (!open || !produto) return null;

  // Calcula o saldo exibido após cada movimentação (lista já está desc)
  const movsComSaldo = useMemo(() => {
    let saldo = Number(produto.estoque || 0);
    return (movimentacoes || []).map((m) => {
      const atual = saldo; // saldo "após" a última movimentação registrada
      // voltando no tempo: desfaz o efeito dessa movimentação
      saldo = m.tipo === "entrada"
        ? saldo - Number(m.quantidade || 0)
        : saldo + Number(m.quantidade || 0);
      return { ...m, saldo: atual };
    });
  }, [movimentacoes, produto.estoque]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl p-8 relative animate-modalSlideIn">
        <button
          className="absolute right-8 top-7 text-gray-500 hover:text-[#004072] dark:text-gray-200 dark:hover:text-white"
          onClick={onClose}
        >
          Fechar
        </button>

        <h2 className="text-2xl font-bold mb-4 text-[#004072] dark:text-white">
          Histórico de Movimentações
        </h2>

        <div className="mb-2 text-[#004072] dark:text-gray-300 font-semibold">
          Produto: <span className="font-normal">{produto.nome}</span>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-300">
            Carregando...
          </div>
        ) : movsComSaldo.length === 0 ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-300">
            Nenhuma movimentação registrada.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[400px]">
            <table className="min-w-full bg-white dark:bg-gray-900 border dark:border-gray-800">
              <thead>
                <tr>
                  <th className="p-3 font-bold text-[#004072] dark:text-white text-center">
                    Data
                  </th>
                  <th className="p-3 font-bold text-[#004072] dark:text-white text-center">
                    Tipo
                  </th>
                  <th className="p-3 font-bold text-[#004072] dark:text-white text-center">
                    Quantidade
                  </th>
                  <th className="p-3 font-bold text-[#004072] dark:text-white text-center">
                    Observação
                  </th>
                  <th className="p-3 font-bold text-[#004072] dark:text-white text-center">
                    Saldo
                  </th>
                </tr>
              </thead>
              <tbody>
                {movsComSaldo.map((m) => (
                  <tr key={m.id} className="border-t dark:border-gray-700">
                    <td className="p-3 align-middle text-center">
                      {formatDate(m.data)}
                    </td>
                    <td
                      className={`p-3 align-middle text-center font-bold ${
                        m.tipo === "entrada"
                          ? "text-green-700 dark:text-green-400"
                          : "text-red-700 dark:text-red-400"
                      }`}
                    >
                      {m.tipo === "entrada" ? "Entrada" : "Saída"}
                    </td>
                    <td className="p-3 align-middle text-center">
                      {m.quantidade}
                    </td>
                    <td className="p-3 align-middle text-center">
                      {m.observacao || "-"}
                    </td>
                    <td className="p-3 align-middle text-center font-semibold">
                      {m.saldo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
        @keyframes modalSlideIn {
          0% { opacity:0; transform: translateY(60px) scale(.97);}
          100% { opacity:1; transform: translateY(0) scale(1);}
        }
      `}</style>
    </div>
  );
};

export default HistoricoMovimentacoesModal;
